import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import { graphql } from 'react-relay';

import { SpotlightTarget } from '@atlaskit/onboarding';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { CustomTitle } from '@atlaskit/navigation-system/top-nav-items';
import { SideNavToggleButton } from '@atlaskit/navigation-system/layout/side-nav';
import { TopNavStart, TopNavMiddle, TopNavEnd } from '@atlaskit/navigation-system/layout/top-nav';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { APP_NAV_GLOBAL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd';
import { APP_NAVIGATION_METRIC } from '@confluence/browser-metrics/entry-points/app-navigation.metric';
import {
	PERFORMANCE_SUBJECT_navigation,
	PERFORMANCE_SUBJECT_navigationFMP,
} from '@confluence/performance/entry-points/constants';
import { PerformanceEnd } from '@confluence/performance/entry-points/PerformanceEnd';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { ShortcutVisualizer } from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import { UNIVERSAL_SIDE_NAVIGATION_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { EditionAwareness } from '@confluence/edition-awareness';
import { fg } from '@confluence/feature-gating';
import { EditionAwarenessUpsellExperiment } from '@confluence/experiment-edition-awareness-for-standard-to-premium';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive';
import { useContentTreeSelectionActions } from '@confluence/content-tree-actions/entry-points/store';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId';

import type { useCustomTheme } from './theme/useCustomTheme';
import { AppSwitcherButton } from './AppSwitcherButton';
import { DeveloperButtons } from './DeveloperButtons';
import { SiteLogoButton } from './SiteLogoButton';
import { HelpButton } from './HelpButton';
import { NotificationsButton } from './NotificationsButton';
import { SettingsButton } from './SettingsButton';
import { AccountButton } from './AccountButton';
import { LoginButton } from './LoginButton';
import { CreateComponent } from './CreateComponent';
import { SearchBar } from './SearchBar';
import { RovoButton } from './RovoButton';
import type {
	TopNavigationQuery$data,
	TopNavigationQuery$variables,
} from './__generated__/TopNavigationQuery.graphql';

const TopNavigationQuery = graphql`
	query TopNavigationQuery($includeNav4AdminOptIn: Boolean!) {
		siteConfiguration {
			customSiteLogo
		}
		siteSettings {
			siteTitle
			showApplicationTitle
			isNav4OptedIn @include(if: $includeNav4AdminOptIn)
		}
		siteDescription {
			logoUrl
		}
		user: confluence_atlassianUser(current: true) {
			id
			displayName
			emails {
				value
				isPrimary
			}
			photos {
				value
			}
		}
	}
`;

type TopNavigationProps = {
	defaultCollapsed: boolean;
	customTheme?: ReturnType<typeof useCustomTheme>;
};

export const TopNavigation = ({ defaultCollapsed, customTheme }: TopNavigationProps) => {
	const { data, loading, error } = useQueryInterop<
		TopNavigationQuery$data,
		TopNavigationQuery$variables
	>(TopNavigationQuery, {
		variables: {
			includeNav4AdminOptIn: fg('confluence_nav4_ga_phase_1'),
		},
	});

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	if (loading) {
		return null;
	}

	return (
		<>
			<TopNavigationInner
				data={data}
				defaultCollapsed={defaultCollapsed}
				customTheme={customTheme}
			/>
			{error && <ErrorDisplay error={error} />}
		</>
	);
};

type TopNavigationInnerProps = {
	data: TopNavigationQuery$data | undefined;
	defaultCollapsed: boolean;
	customTheme?: ReturnType<typeof useCustomTheme>;
};

const isAtlassianStaff = (data?: TopNavigationQuery$data) => {
	return (
		data?.user?.emails?.some(
			(email) =>
				email?.value &&
				(email.value.endsWith('@atlassian.com') ||
					email.value.endsWith('@atlassian.com.invalid') ||
					email.value.endsWith('@usersinbuckets.com')),
		) || false
	);
};

const TopNavigationInner = ({ data, defaultCollapsed, customTheme }: TopNavigationInnerProps) => {
	const isFhsEnabled = fg('navx-2733-convert-confluence-fhs-rollout')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFhsEnabled()
		: fg('navx-full-height-sidebar');

	const { isLoggedIn, cloudId, isLicensed } = useSessionData();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isOnEditRoute = useIsEditorPage();
	const isAtlassianStaffMemoized = useMemo(() => isAtlassianStaff(data), [data]);

	const { uuid, isInitial } = getUniquePageLoadId();
	const hasCustomTheme = !!customTheme?.highlightColor || !!customTheme?.backgroundColor;

	const [customLogoSize, setCustomLogoSize] = useState<{ width: number; height: number } | null>(
		null,
	);

	const handleOnLoadCustomLogo = useCallback(
		(event: React.SyntheticEvent<HTMLImageElement, Event>) => {
			const { naturalWidth, naturalHeight } = event.currentTarget;
			setCustomLogoSize({ width: naturalWidth, height: naturalHeight });
		},
		[],
	);

	useEffect(() => {
		// see: https://hello.jira.atlassian.cloud/browse/NAVX-2056
		if (data && isInitial && uuid) {
			const hasCustomSiteTitle =
				!!data.siteSettings?.showApplicationTitle && !!data.siteSettings?.siteTitle;
			const hasCustomLogo =
				!!data.siteConfiguration?.customSiteLogo && !!data.siteDescription?.logoUrl;

			// If there is a custom logo, but we haven't captured its size, do not log the event yet, otherwise continue
			if (hasCustomLogo && !customLogoSize) {
				return;
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'globalNavigation',
					action: 'viewed',
					actionSubject: 'topNav',
					actionSubjectId: 'customTheme',
					attributes: {
						cloudId,
						hasCustomLogo,
						hasCustomSiteTitle,
						hasCustomHighlightColor: !!customTheme?.highlightColor,
						hasCustomBGColor: !!customTheme?.backgroundColor,
						...(hasCustomLogo && customLogoSize ? customLogoSize : undefined),
						...(hasCustomSiteTitle && {
							customSiteTitleLength: data.siteSettings?.siteTitle.length,
						}),
					},
				},
			}).fire();
		}
	}, [cloudId, createAnalyticsEvent, customLogoSize, data, isInitial, customTheme, uuid]);

	const getShortcutVisualizer = useCallback(
		(label: string) => (
			<ShortcutVisualizer
				contentBefore={label}
				shortcut={UNIVERSAL_SIDE_NAVIGATION_SHORTCUT}
				isEditorShortcut={isOnEditRoute}
				isTooltip
			/>
		),
		[isOnEditRoute],
	);
	const { exitMultiSelect } = useContentTreeSelectionActions();
	const [isMultiSelectActive] = useIsMultiSelectActive();

	const handleMultiSelectOnCollapse = useCallback(() => {
		if (isMultiSelectActive) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'globalNavigation',
					action: 'closed',
					actionSubject: 'bulkActionsMenu',
					attributes: {
						method: 'collapseSideNavButton',
					},
				},
			}).fire();
			exitMultiSelect();
		}
	}, [isMultiSelectActive, exitMultiSelect, createAnalyticsEvent]);

	const sideNavToggleButton = (
		<SpotlightTarget name="nav4-spotlight-1">
			<SideNavToggleButton
				defaultCollapsed={isFhsEnabled ? undefined : defaultCollapsed}
				collapseLabel={
					isFhsEnabled
						? formatMessage(i18n.sideNavToggleButtonCollapseLabel)
						: getShortcutVisualizer(formatMessage(i18n.sideNavToggleButtonCollapseLabel))
				}
				expandLabel={
					isFhsEnabled
						? formatMessage(i18n.sideNavToggleButtonExpandLabel)
						: getShortcutVisualizer(formatMessage(i18n.sideNavToggleButtonExpandLabel))
				}
				onClick={handleMultiSelectOnCollapse}
			/>
		</SpotlightTarget>
	);

	return (
		<>
			<TopNavStart
				sideNavToggleButton={
					fg('platform_dst_nav4_side_nav_toggle_button_slot') ? sideNavToggleButton : undefined
				}
			>
				{fg('platform_dst_nav4_side_nav_toggle_button_slot') ? null : sideNavToggleButton}
				<AppSwitcherButton />
				<DeveloperButtons isAtlassianStaff={isAtlassianStaffMemoized} />
				<SiteLogoButton
					siteConfiguration={data?.siteConfiguration}
					siteSettings={data?.siteSettings}
					siteDescription={data?.siteDescription}
					onLoadCustomLogo={handleOnLoadCustomLogo}
				/>
				{data?.siteSettings?.showApplicationTitle ? (
					<CustomTitle>{data?.siteSettings.siteTitle}</CustomTitle>
				) : null}
			</TopNavStart>
			<TopNavMiddle>
				<SearchBar />
				<CreateComponent />
			</TopNavMiddle>
			<TopNavEnd>
				<EditionAwareness />
				{fg('edition_awareness_standard_upsell_confluence_ab_fg') && (
					<EditionAwarenessUpsellExperiment navVersion="nav4" />
				)}
				<RovoButton />
				<NotificationsButton />
				<HelpButton />
				<SettingsButton />
				{isLoggedIn && data?.user ? (
					<SpotlightTarget name="nav-profile">
						<AccountButton user={data.user} />
					</SpotlightTarget>
				) : (
					<LoginButton />
				)}
			</TopNavEnd>
			<ExperienceSuccess name={APP_NAV_GLOBAL_EXPERIENCE} attributes={{ navVersion: '4' }} />
			<PageSegmentLoadEnd
				metric={APP_NAVIGATION_METRIC}
				customData={{
					isAnonymous: !isLoggedIn,
					isLicensed,
					hasCustomTheme,
				}}
			/>
			<PerformanceEnd
				subject={PERFORMANCE_SUBJECT_navigation}
				subjectId="NavigationLoading"
				includeFeatureFlags
				details={nav4Details}
			/>
			<PerformanceEnd
				subject={PERFORMANCE_SUBJECT_navigationFMP}
				subjectId="NavigationFMP"
				includeFeatureFlags
				details={nav4Details}
			/>
		</>
	);
};

const i18n = defineMessages({
	sideNavToggleButtonCollapseLabel: {
		id: 'app-navigation.top-navigation.side-nav-toggle-button-collapse-label',
		defaultMessage: 'Collapse sidebar',
		description:
			'Label to use for side nav toggle button when it will collapse the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
	},
	sideNavToggleButtonExpandLabel: {
		id: 'app-navigation.top-navigation.side-nav-toggle-button-expand-label',
		defaultMessage: 'Expand sidebar',
		description:
			'Label to use for side nav toggle button when it will expand the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
	},
});

const nav4Details = { navVersion: '4' };
